import React from 'react';
import { compose } from 'redux';
import { ResourceLanguage } from 'i18next';
import QuillImporter from 'devextreme/ui/html_editor/quill_importer';
import {
  withBaseAppProvider,
  ReduxModule
} from '../BaseAppProvider/BaseAppProvider';
import { MXAuth } from '../XAuth/XAuth';
import { MwithDevExtreme } from '../withDevExtreme/withDevExtreme';
import { MwithGlobalErrorBoundary } from '../withGlobalErrorBoundary/withGlobalErrorBoundary';
import {
  NavItem,
  HeaderItem,
  DynamicNavItemFetchFn
} from '../withNavLayout/withNavLayout';
import { MXAppTranslations } from './translations';
import { initi18nTranslations } from './utils/translations';
import { getSideNavModule } from './utils/navigation';
import { getEnumsModule } from './withEnums/withEnums';
import { reduxProviderHocSetup } from './redux/providerHocSetup';

export { TranslationsContext, translationsHocSetup } from './translations';
export {
  createWithEnumsHookContext,
  EnumsHookContext
} from './withEnums/withEnumsHookContext';

const quill = QuillImporter.getQuill();
const bold = quill.import('formats/bold');
bold.tagName = 'b';
quill.register(bold, true);

export type HOC = (
  WrappedComponent: React.FC<any>
) => (props: any) => JSX.Element;

export interface Translations {
  [language: string]: ResourceLanguage;
}

export interface Module {
  id?: string;
  apiConfigFields?: string[];
  reduxModules?: ReduxModule[];
  hoc?: HOC;
  enumsEndpoint?: string;
  navItems?: NavItem[];
  headerItems?: HeaderItem[];
  translations?: Translations;
  dynamicNavItemsProviders?: DynamicNavItemFetchFn[];
}

interface ConnectXAppWrapperArgs {
  modules?: Module[];
  homePath?: string;
}

export const connectXAppWrapperBasic = (
  { modules }: ConnectXAppWrapperArgs = { modules: [] }
): React.FC<any> => {
  const reduxModules = modules?.map((x) => x.reduxModules || []).flat();
  const hocs = modules
    ? modules.filter((x) => x.hoc !== undefined).map((x) => x.hoc)
    : [];

  const withReduxProvider = reduxProviderHocSetup(reduxModules);

  return compose(
    withBaseAppProvider,
    withReduxProvider,
    ...(hocs as HOC[])
  )(() => null) as React.FC<any>;
};

const setUpModules = ({ modules, homePath }: ConnectXAppWrapperArgs = {}) => {
  if (!modules) return [];

  const defaultModules: Module[] = [
    MXAppTranslations,
    MwithGlobalErrorBoundary,
    MwithDevExtreme,
    MXAuth,
    getEnumsModule(modules || [])
  ];

  const combinedModules = modules
    ? [...defaultModules, ...modules]
    : defaultModules;

  initi18nTranslations(combinedModules);

  return [
    ...combinedModules,
    getSideNavModule({
      combinedModules,
      homePath
    })
  ];
};

export const connectXAppWrapper = (settings: ConnectXAppWrapperArgs = {}) => {
  const determinedModules = setUpModules(settings);

  return connectXAppWrapperBasic({
    modules: determinedModules
  }) as React.FC<any>;
};
